import { MarkOptionsArg, PerformanceLogPayload, Timing } from 'types/instrumentation';
import pushEvents from 'vsb-sdk/api/push-events';

const sdkLoad = 'vsb_sdk_js_load';
const timings: Timing = {};
const DEBUG_PAYLOAD = '__debug_vsb';

// handle different levels of performance API support
export const isMarkable =
  typeof performance.mark === 'function' &&
  typeof performance.measure === 'function' &&
  typeof performance.getEntries === 'function' &&
  typeof performance.getEntriesByName === 'function' &&
  typeof performance.getEntriesByType === 'function' &&
  typeof performance.clearMeasures === 'function';

const generatePageLoadProfile = () => {
  const page = {
    TTFB: 0,
    TTFI: 0
  };

  /* START GENAI@GHCOPILOT */
  const navigationEntry = performance.getEntriesByType(
    'navigation'
  )[0] as PerformanceNavigationTiming;

  page.TTFB = navigationEntry.responseStart - navigationEntry.startTime;
  page.TTFI = navigationEntry.domInteractive - navigationEntry.startTime;
  /* END GENAI@GHCOPILOT */
  return page;
};

const handleBrowserAPIs = (api: { [key: string]: string }): string | { [key: string]: string } => {
  // eslint-disable-next-line
  if (api) {
    const ret: { [key: string]: string } = {};
    for (const k in api) {
      if (['string', 'number'].indexOf(typeof api[k]) !== -1) {
        ret[k] = api[k];
      }
    }

    return ret;
  }

  return 'not supported';
};

// called from merchant/PSP domain (visa-sdk) as well as Visa domain (communicator)
export const buildClientProfile = (correlationId: string | undefined) => {
  if (isMarkable) {
    const CR = correlationId || 'cr_not_found';
    const nav = navigator as Navigator;
    const payload = {
      correlationId: CR,
      device: {
        // @ts-ignore - Navigation type is outdated
        conn: handleBrowserAPIs(nav.connection), // blink-only
        cpuCores: nav.hardwareConcurrency,
        locale: nav.language,
        // @ts-ignore - Navigation type is outdated
        mem: nav.deviceMemory ? `${nav.deviceMemory}GB` : 'not supported',
        ua: nav.userAgent
      },
      domains: {},
      page: generatePageLoadProfile()
    };

    /* START GENAI@GHCOPILOT */
    const latencyByDomain: {
      [domain: string]: {
        latency: number;
        totalRequests: number;
        averageLatency: number;
        assets: {
          url: string;
          latency: number;
        }[];
      };
    } = {};

    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    resources.forEach((resource: PerformanceResourceTiming) => {
      const latency: number = resource.responseEnd - resource.fetchStart;
      const domain = new URL(resource.name).hostname as string;

      // eslint-disable-next-line
      if (!latencyByDomain[domain]) {
        latencyByDomain[domain] = {
          assets: [],
          averageLatency: 0,
          latency: 0,
          totalRequests: 0
        };
      }

      latencyByDomain[domain].latency += latency;
      latencyByDomain[domain].totalRequests++;
      latencyByDomain[domain].assets.push({ url: resource.name, latency });
    });

    for (const domain in latencyByDomain) {
      latencyByDomain[domain].averageLatency = Math.round(
        latencyByDomain[domain].latency / latencyByDomain[domain].totalRequests
      );

      // @ts-ignore - no need for aggregate latency
      delete latencyByDomain[domain].latency;
    }

    /* END GENAI@GHCOPILOT */
    payload.domains = latencyByDomain;
    return payload;
  }

  return null;
};

export const buildEventPayload = (args: {
  key: string;
  options: MarkOptionsArg;
  timings?: Timing;
}): PerformanceLogPayload | null => {
  let measure;
  let mt;
  let { key } = args;
  const { options, timings } = args;
  const { failure, reason } = options;
  const endKey = `${key}_end`;
  const startKey = `${key}_start`;
  let etime;
  let stime;
  let ttaken = NaN;

  if (timings) {
    timings[endKey] = timings[endKey] || Date.now();
    isMarkable && performance.mark(endKey);
    etime = timings[endKey] || NaN;
    stime = timings[startKey] || NaN;
    ttaken = etime - stime;
  }

  if (!options.manualTiming) {
    // undef or `bool:true`
    try {
      measure = isMarkable && performance.measure(key, startKey, endKey);
      mt = measure && measure.toJSON();
    } catch (err) {
      /* istanbul ignore next */
      // eslint-disable-next-line no-console
      console.warn(err);
    }
  }

  if (isNaN(ttaken)) {
    // eslint-disable-next-line no-console
    console.warn(`refusing to send bad performance data for ${key}`); // eslint-disable no-console
    return null;
  }

  key += failure ? '_FAILURE' : '_SUCCESS';

  /* eslint-disable sort-keys */
  return {
    E: 'VSB_V2_PERF_BATCH',
    [key]: measure ? mt.duration : ttaken,
    reason: reason || undefined
  };
};

const queue: PerformanceLogPayload[] = [];

/* START GENAI@GHCOPILOT */
//rearranged to address lint error
export const pushPerfEvents = () => {
  if (isMarkable) {
    const batchEvent: PerformanceLogPayload = {
      E: 'VSB_V2_PERF_BATCH'
    };

    while (queue.length) {
      const entry = queue.shift();
      for (const key in entry) {
        batchEvent[key] = entry[key];
      }
    }

    pushEvents(batchEvent);
  }
};
/* END GENAI@GHCOPILOT */

const queueLogEntry = (
  eventName: string,
  stime: number,
  etime: number,
  options?: MarkOptionsArg
) => {
  options = options || { doFlush: false, failure: false, manualTiming: false, reason: undefined };
  const startKey = `${eventName}_start`;
  const endKey = `${eventName}_end`;
  timings[startKey] = stime;
  timings[endKey] = etime;

  const payload = buildEventPayload({
    key: eventName,
    options,
    timings
  });

  /* START GENAI@GHCOPILOT */
  if (payload) {
    queue.push(payload);
    options.doFlush && pushPerfEvents();
  }
  /* END GENAI@GHCOPILOT */
};

// RCA: performance API returns unusual duration value
/* istanbul ignore next */
const isSaneDuration = (n: number): boolean => {
  return n > 2 && n < 60000; // prevent garbage from polluting logging.
};

export const markStart = (
  key: string,
  options: MarkOptionsArg = {
    doFlush: false,
    failure: false,
    onlyOnce: false,
    reason: undefined
  }
) => {
  if (isMarkable) {
    /*eslint-disable */
    console.log(`>>>>>>>>> markStart: ${key}`);
    const startKey = `${key}_start`;

    if (options.onlyOnce && performance.getEntriesByName(startKey).length > 0) {
      return;
    }

    performance.mark(startKey);
    timings[startKey] = Date.now();
  }
};

export const markEnd = (
  key: string,
  options: MarkOptionsArg = {
    doFlush: false,
    failure: false,
    onlyOnce: false,
    reason: undefined
  }
) => {
  if (isMarkable) {
    /*eslint-disable */
    console.log(`>>>>>>>>> markEnd: ${key}`);
    const startKey = `${key}_start`;
    const endKey = `${key}_end`;

    if (options.onlyOnce && performance.getEntriesByName(startKey).length > 0) {
      return;
    }

    performance.mark(endKey);
    timings[endKey] = Date.now();
    queueLogEntry(key, timings[startKey], timings[endKey], options);
  }
};

export const asyncMarkWrapper = async (apiName: string, brand: string, promise: Promise<any>) => {
  markStart(`vsb_${apiName}_${brand}`);
  const ret = await promise;
  markEnd(`vsb_${apiName}_${brand}`);
  return ret;
};

/* START GENAI@GHCOPILOT */
export const measure = (key: string, options: MarkOptionsArg = {}) => {
  if (isMarkable) {
    const startKey = `${key}_start`;
    const endKey = `${key}_end`;

    if (
      performance.getEntriesByName(startKey).length === 0 ||
      performance.getEntriesByName(endKey).length === 0
    ) {
      return;
    }

    const startMark = performance.getEntriesByName(startKey)[0];
    const endMark = performance.getEntriesByName(endKey)[0];
    const duration = endMark.startTime - startMark.startTime;

    if (isSaneDuration(duration)) {
      timings[key] = duration;
      queueLogEntry(key, startMark.startTime, endMark.startTime, options);
    }
  }
};
/* END GENAI@GHCOPILOT */

export const collectDebug = (err?: unknown, notes?: string) => {
  if (isMarkable) {
    try {
      const profile = buildClientProfile(undefined); // no CR available here

      if (profile) {
        const storageStr = localStorage.getItem(DEBUG_PAYLOAD);
        const storage = storageStr ? JSON.parse(storageStr) : [];
        storage.push({
          date: Date.now(),
          err,
          notes,
          profile
        });

        // cache for bad connections, broken communciator iframe, etc.
        localStorage.setItem(DEBUG_PAYLOAD, JSON.stringify(storage));
      }
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }
};

export const pushDebug = () => {
  if (isMarkable) {
    const debugData = localStorage.getItem(DEBUG_PAYLOAD);

    if (debugData) {
      localStorage.removeItem(DEBUG_PAYLOAD);
      const pushPayload = {
        E: 'VSB_DEBUG_DATA_PAYLOAD',
        payload: JSON.parse(debugData)
      };

      pushEvents(pushPayload);
    }
  }
};

export const getEmvcoLoadTiming = () => {
  if (isMarkable) {
    const reVsbSdk = /v2\/sdk\.js\?.*/gi;
    const res = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

    for (let i = 0, n = res.length; i < n; i++) {
      const timing = res[i];

      /* START GENAI@GHCOPILOT */
      if (timing.initiatorType === 'script' && reVsbSdk.test(timing.name)) {
        queueLogEntry(sdkLoad, 1, timing.duration, { manualTiming: true, doFlush: false });
      } else if (
        timing.initiatorType === 'script' &&
        timing.name.includes('srcsdk.mastercard.js')
      ) {
        queueLogEntry('vsb_ma_sdk_js_load', 1, timing.duration, {
          manualTiming: true,
          doFlush: false
        });
      } else if (timing.initiatorType === 'iframe' && timing.name.includes('orch-communicator')) {
        queueLogEntry('vsb_orch_communicator_iframe_load', 1, timing.duration, {
          manualTiming: true,
          doFlush: false
        });
      } else if (
        timing.initiatorType === 'iframe' &&
        timing.name.includes('mastercard.com/sdk/communicator-frame')
      ) {
        queueLogEntry('vsb_ma_communicator_iframe_load', 1, timing.duration, {
          manualTiming: true,
          doFlush: false
        });
      }
      // TODO: add other EMVCO timings as enabled
      /* END GENAI@GHCOPILOT */
    }
  }
};
